import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert, Spinner, Modal, Pagination } from 'react-bootstrap';
import { apiService } from '../../services/api';

interface Vehicle {
  id: string;
  plateNumber: string;
  ownerName: string;
  ownerPhone: string;
  ownerAddress?: string;
  vehicleType: 'car' | 'motorcycle' | 'truck';
  brand: string;
  model: string;
  color: string;
  registrationDate: string;
  status: 'active' | 'blacklisted' | 'vip';
  currentSpace?: string;
  assignedSpaceId?: string;
  isSharedSpace?: boolean;
  additionalPlates?: string[]; // 共用車位的額外車牌列表
  startDate?: string;
  endDate?: string;
  totalVisits: number;
  lastVisit?: string;
  notes?: string;
}

interface FormData {
  plateNumber: string;
  ownerName: string;
  ownerPhone: string;
  ownerAddress: string;
  vehicleType: 'car' | 'motorcycle' | 'truck';
  brand: string;
  model: string;
  color: string;
  status: 'active' | 'blacklisted' | 'vip';
  assignedSpaceId: string;
  isSharedSpace: boolean;
  additionalPlates: string[]; // 共用車位的額外車牌列表
  startDate: string;
  endDate: string;
  notes: string;
}

interface SearchFilters {
  plateNumber: string;
  ownerName: string;
  ownerPhone: string;
  brand: string;
  color: string;
  status: string;
  vehicleType: string;
  registrationDateFrom: string;
  registrationDateTo: string;
  lastVisitFrom: string;
  lastVisitTo: string;
}

const VehicleRegistration: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchPlate, setSearchPlate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>('all');
  const vehiclesPerPage = 10;
  
  // 增強搜尋篩選狀態
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    plateNumber: '',
    ownerName: '',
    ownerPhone: '',
    brand: '',
    color: '',
    status: 'all',
    vehicleType: 'all',
    registrationDateFrom: '',
    registrationDateTo: '',
    lastVisitFrom: '',
    lastVisitTo: ''
  });
  
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  // 表單狀態
  const [formData, setFormData] = useState<FormData>({
    plateNumber: '',
    ownerName: '',
    ownerPhone: '',
    ownerAddress: '',
    vehicleType: 'car',
    brand: '',
    model: '',
    color: '',
    status: 'active',
    assignedSpaceId: '',
    isSharedSpace: false,
    additionalPlates: [], // 共用車位的額外車牌列表
    startDate: new Date().toISOString().split('T')[0], // 今天的日期
    endDate: '',
    notes: ''
  });

  // 載入車輛數據
  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getVehicles({
        page: currentPage,
        limit: vehiclesPerPage,
        search: searchPlate,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        type: vehicleTypeFilter !== 'all' ? vehicleTypeFilter : undefined
      });
      
      if (response.success && response.data) {
        // 轉換API數據格式到前端格式
        const formattedVehicles: Vehicle[] = response.data.items?.map((vehicle: any) => ({
          id: vehicle._id || vehicle.id,
          plateNumber: vehicle.licensePlate || vehicle.plateNumber,
          ownerName: vehicle.owner?.name || vehicle.ownerName || '未知',
          ownerPhone: vehicle.owner?.phone || vehicle.ownerPhone || '未提供',
          vehicleType: vehicle.type || vehicle.vehicleType || 'car',
          brand: vehicle.brand || '未知',
          model: vehicle.model || '未知',
          color: vehicle.color || '未知',
          registrationDate: vehicle.createdAt ? new Date(vehicle.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          status: vehicle.status || 'active',
          currentSpace: vehicle.currentSpace?.number || vehicle.currentSpace,
          totalVisits: vehicle.totalVisits || 0,
          lastVisit: vehicle.lastVisit ? new Date(vehicle.lastVisit).toLocaleString() : undefined
        })) || [];
        
        setVehicles(formattedVehicles);
      } else {
        setError('載入車輛數據失敗');
      }
    } catch (error: any) {
      console.error('載入車輛失敗:', error);
      setError('載入車輛數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'vip': return 'warning';
      case 'blacklisted': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '正常';
      case 'vip': return 'VIP';
      case 'blacklisted': return '黑名單';
      default: return '未知';
    }
  };

  const getVehicleTypeText = (type: string) => {
    switch (type) {
      case 'car': return '汽車';
      case 'motorcycle': return '機車';
      case 'truck': return '卡車';
      default: return '未知';
    }
  };

  // 過濾車輛 - 增強版搜尋邏輯
  const filteredVehicles = vehicles.filter(vehicle => {
    // 基本搜尋
    const matchesPlate = vehicle.plateNumber.toLowerCase().includes(searchPlate.toLowerCase());
    const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;
    const matchesType = vehicleTypeFilter === 'all' || vehicle.vehicleType === vehicleTypeFilter;
    
    // 進階搜尋
    const matchesAdvancedPlate = vehicle.plateNumber.toLowerCase().includes(searchFilters.plateNumber.toLowerCase());
    const matchesOwnerName = vehicle.ownerName.toLowerCase().includes(searchFilters.ownerName.toLowerCase());
    const matchesOwnerPhone = vehicle.ownerPhone.includes(searchFilters.ownerPhone);
    const matchesBrand = vehicle.brand.toLowerCase().includes(searchFilters.brand.toLowerCase());
    const matchesColor = vehicle.color.toLowerCase().includes(searchFilters.color.toLowerCase());
    const matchesAdvancedStatus = searchFilters.status === 'all' || vehicle.status === searchFilters.status;
    const matchesAdvancedType = searchFilters.vehicleType === 'all' || vehicle.vehicleType === searchFilters.vehicleType;
    
    // 日期篩選
    let matchesRegistrationDate = true;
    if (searchFilters.registrationDateFrom) {
      matchesRegistrationDate = matchesRegistrationDate && new Date(vehicle.registrationDate) >= new Date(searchFilters.registrationDateFrom);
    }
    if (searchFilters.registrationDateTo) {
      matchesRegistrationDate = matchesRegistrationDate && new Date(vehicle.registrationDate) <= new Date(searchFilters.registrationDateTo);
    }
    
    let matchesLastVisitDate = true;
    if (vehicle.lastVisit) {
      if (searchFilters.lastVisitFrom) {
        matchesLastVisitDate = matchesLastVisitDate && new Date(vehicle.lastVisit) >= new Date(searchFilters.lastVisitFrom);
      }
      if (searchFilters.lastVisitTo) {
        matchesLastVisitDate = matchesLastVisitDate && new Date(vehicle.lastVisit) <= new Date(searchFilters.lastVisitTo);
      }
    }
    
    return matchesPlate && matchesStatus && matchesType && 
           matchesAdvancedPlate && matchesOwnerName && matchesOwnerPhone && 
           matchesBrand && matchesColor && matchesAdvancedStatus && 
           matchesAdvancedType && matchesRegistrationDate && matchesLastVisitDate;
  });

  // 分頁
  const indexOfLastVehicle = currentPage * vehiclesPerPage;
  const indexOfFirstVehicle = indexOfLastVehicle - vehiclesPerPage;
  const currentVehicles = filteredVehicles.slice(indexOfFirstVehicle, indexOfLastVehicle);
  const totalPages = Math.ceil(filteredVehicles.length / vehiclesPerPage);

  const handleAddVehicle = () => {
    setSelectedVehicle(null);
    setIsEditing(false);
    setFormData({
      plateNumber: '',
      ownerName: '',
      ownerPhone: '',
      ownerAddress: '',
      vehicleType: 'car',
      brand: '',
      model: '',
      color: '',
      status: 'active',
      assignedSpaceId: '',
      isSharedSpace: false,
      additionalPlates: [],
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      notes: ''
    });
    setShowModal(true);
  };

  // 重置搜尋篩選
  const resetFilters = () => {
    setSearchPlate('');
    setStatusFilter('all');
    setVehicleTypeFilter('all');
    setSearchFilters({
      plateNumber: '',
      ownerName: '',
      ownerPhone: '',
      brand: '',
      color: '',
      status: 'all',
      vehicleType: 'all',
      registrationDateFrom: '',
      registrationDateTo: '',
      lastVisitFrom: '',
      lastVisitTo: ''
    });
    setCurrentPage(1);
  };

  // 更新搜尋篩選
  const updateSearchFilter = (field: keyof SearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsEditing(true);
    setFormData({
      plateNumber: vehicle.plateNumber,
      ownerName: vehicle.ownerName,
      ownerPhone: vehicle.ownerPhone,
      ownerAddress: vehicle.ownerAddress || '',
      vehicleType: vehicle.vehicleType,
      brand: vehicle.brand,
      model: vehicle.model,
      color: vehicle.color,
      status: vehicle.status,
      assignedSpaceId: vehicle.assignedSpaceId || '',
      isSharedSpace: vehicle.isSharedSpace || false,
      additionalPlates: vehicle.additionalPlates || [],
      startDate: vehicle.startDate || '',
      endDate: vehicle.endDate || '',
      notes: vehicle.notes || ''
    });
    setShowModal(true);
  };

  const handleSaveVehicle = async () => {
    try {
      // 驗證必填欄位
      if (!formData.plateNumber || !formData.ownerName || !formData.ownerPhone) {
        alert('請填寫所有必填欄位');
        return;
      }

      // 構建發送到後端的數據格式
      const vehicleData = {
        licensePlate: formData.plateNumber,
        brand: formData.brand,
        model: formData.model,
        color: formData.color,
        ownerName: formData.ownerName,
        ownerPhone: formData.ownerPhone,
        ownerEmail: '', // 可選字段，設為空字符串
        isVip: formData.status === 'vip',
        status: (formData.status || 'active') as 'active' | 'blacklisted' | 'vip' | 'inactive',
        assignedSpaceId: formData.assignedSpaceId,
        isSharedSpace: formData.isSharedSpace,
        additionalPlates: formData.isSharedSpace ? formData.additionalPlates.filter(plate => plate.trim() !== '') : [],
        startDate: formData.startDate,
        endDate: formData.endDate,
        notes: formData.notes
      };

      if (isEditing && selectedVehicle) {
        // 更新車輛
        await apiService.updateVehicle(selectedVehicle.id, vehicleData);
        alert('車輛更新成功！');
      } else {
        // 新增車輛
        await apiService.createVehicle(vehicleData);
        alert('車輛新增成功！');
      }
      
      // 重新載入車輛列表
      await loadVehicles();
      
      // 關閉模態框並重置表單
      setShowModal(false);
      setFormData({
        plateNumber: '',
        ownerName: '',
        ownerPhone: '',
        ownerAddress: '',
        vehicleType: 'car',
        brand: '',
        model: '',
        color: '',
        status: 'active',
        assignedSpaceId: '',
        isSharedSpace: false,
        additionalPlates: [],
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        notes: ''
      });
      setSelectedVehicle(null);
      setIsEditing(false);
    } catch (error: any) {
      console.error('保存車輛失敗:', error);
      alert(`保存失敗：${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    if (window.confirm('確定要刪除這輛車輛嗎？')) {
      try {
        await apiService.deleteVehicle(vehicleId);
        alert('車輛刪除成功！');
        // 重新載入車輛列表
        await loadVehicles();
      } catch (error: any) {
        console.error('刪除車輛失敗:', error);
        alert(`刪除失敗：${error.response?.data?.message || error.message}`);
      }
    }
  };

  // 處理車輛匯入
  const handleImportVehicles = async () => {
    if (!importFile) return;

    setImportLoading(true);
    try {
      const result = await apiService.importVehicles(importFile);
      
      if (result.success) {
        alert(`匯入成功！共匯入 ${result.data?.imported || 0} 筆車輛資料`);
        setShowImportModal(false);
        setImportFile(null);
        loadVehicles(); // 重新載入車輛列表
      } else {
        alert(`匯入失敗：${result.message}`);
      }
    } catch (error: any) {
      console.error('匯入車輛失敗:', error);
      alert(`匯入失敗：${error.response?.data?.message || error.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  // 下載匯入範本
  const handleDownloadTemplate = async (format: 'csv' | 'excel') => {
    try {
      const blob = await apiService.downloadVehicleTemplate(format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vehicle_template.${format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下載範本失敗:', error);
      alert('下載範本失敗，請稍後再試');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-car me-3"></i>
          車輛登記
        </h1>
        <div>
          <Button variant="success" onClick={handleAddVehicle} className="me-2">
            <i className="fas fa-plus me-2"></i>
            新增車輛
          </Button>
          <Button variant="info" onClick={() => setShowImportModal(true)} className="me-2">
            <i className="fas fa-upload me-2"></i>
            匯入車輛
          </Button>
          <Button variant="primary" onClick={loadVehicles}>
            <i className="fas fa-sync-alt me-2"></i>
            重新整理
          </Button>
        </div>
      </div>

      {/* 搜尋和過濾 */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-search me-2"></i>
            搜尋與篩選
          </h5>
          <div>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className="me-2"
            >
              <i className={`fas fa-${showAdvancedSearch ? 'minus' : 'plus'} me-1`}></i>
              {showAdvancedSearch ? '收起進階搜尋' : '展開進階搜尋'}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
              <i className="fas fa-undo me-1"></i>
              重置
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* 基本搜尋 */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>快速搜尋車牌號碼</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="請輸入車牌號碼"
                  value={searchPlate}
                  onChange={(e) => setSearchPlate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>狀態篩選</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">全部狀態</option>
                  <option value="active">正常</option>
                  <option value="vip">VIP</option>
                  <option value="blacklisted">黑名單</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>車輛類型</Form.Label>
                <Form.Select
                  value={vehicleTypeFilter}
                  onChange={(e) => setVehicleTypeFilter(e.target.value)}
                >
                  <option value="all">全部類型</option>
                  <option value="car">汽車</option>
                  <option value="motorcycle">機車</option>
                  <option value="truck">卡車</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* 進階搜尋 */}
          {showAdvancedSearch && (
            <>
              <hr />
              <h6 className="text-primary mb-3">
                <i className="fas fa-filter me-2"></i>
                進階搜尋選項
              </h6>
              
              {/* 第一行 - 基本資訊搜尋 */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車牌號碼</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車牌號碼"
                      value={searchFilters.plateNumber}
                      onChange={(e) => updateSearchFilter('plateNumber', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車主姓名</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車主姓名"
                      value={searchFilters.ownerName}
                      onChange={(e) => updateSearchFilter('ownerName', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>聯絡電話</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="聯絡電話"
                      value={searchFilters.ownerPhone}
                      onChange={(e) => updateSearchFilter('ownerPhone', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車輛品牌</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車輛品牌"
                      value={searchFilters.brand}
                      onChange={(e) => updateSearchFilter('brand', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* 第二行 - 車輛詳細資訊 */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車輛顏色</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車輛顏色"
                      value={searchFilters.color}
                      onChange={(e) => updateSearchFilter('color', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>狀態</Form.Label>
                    <Form.Select
                      value={searchFilters.status}
                      onChange={(e) => updateSearchFilter('status', e.target.value)}
                    >
                      <option value="all">全部狀態</option>
                      <option value="active">正常</option>
                      <option value="vip">VIP</option>
                      <option value="blacklisted">黑名單</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車輛類型</Form.Label>
                    <Form.Select
                      value={searchFilters.vehicleType}
                      onChange={(e) => updateSearchFilter('vehicleType', e.target.value)}
                    >
                      <option value="all">全部類型</option>
                      <option value="car">汽車</option>
                      <option value="motorcycle">機車</option>
                      <option value="truck">卡車</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* 第三行 - 日期範圍篩選 */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>註冊日期範圍</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          type="date"
                          value={searchFilters.registrationDateFrom}
                          onChange={(e) => updateSearchFilter('registrationDateFrom', e.target.value)}
                        />
                      </Col>
                      <Col xs="auto" className="d-flex align-items-center">
                        <span className="text-muted">至</span>
                      </Col>
                      <Col>
                        <Form.Control
                          type="date"
                          value={searchFilters.registrationDateTo}
                          onChange={(e) => updateSearchFilter('registrationDateTo', e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>最後造訪日期範圍</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          type="date"
                          value={searchFilters.lastVisitFrom}
                          onChange={(e) => updateSearchFilter('lastVisitFrom', e.target.value)}
                        />
                      </Col>
                      <Col xs="auto" className="d-flex align-items-center">
                        <span className="text-muted">至</span>
                      </Col>
                      <Col>
                        <Form.Control
                          type="date"
                          value={searchFilters.lastVisitTo}
                          onChange={(e) => updateSearchFilter('lastVisitTo', e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Card.Body>
      </Card>

      {/* 車輛表格 */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">車輛列表 ({filteredVehicles.length} 輛車輛)</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {currentVehicles.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-car fa-3x text-muted mb-3"></i>
              <p className="text-muted">沒有找到符合條件的車輛</p>
            </div>
          ) : (
            <Table responsive striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th>車牌號碼</th>
                  <th>車主姓名</th>
                  <th>聯絡電話</th>
                  <th>車輛類型</th>
                  <th>品牌型號</th>
                  <th>顏色</th>
                  <th>狀態</th>
                  <th>分配車位</th>
                  <th>共享車位</th>
                  <th>開始日期</th>
                  <th>結束日期</th>
                  <th>造訪次數</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {currentVehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td className="fw-bold">{vehicle.plateNumber}</td>
                    <td>{vehicle.ownerName}</td>
                    <td>{vehicle.ownerPhone}</td>
                    <td>{getVehicleTypeText(vehicle.vehicleType)}</td>
                    <td>{vehicle.brand} {vehicle.model}</td>
                    <td>{vehicle.color}</td>
                    <td>
                      <Badge bg={getStatusColor(vehicle.status)}>
                        {getStatusText(vehicle.status)}
                      </Badge>
                    </td>
                    <td>{vehicle.assignedSpaceId || '-'}</td>
                    <td>
                      {vehicle.isSharedSpace ? (
                        <Badge bg="info">共享</Badge>
                      ) : (
                        <Badge bg="secondary">專用</Badge>
                      )}
                    </td>
                    <td>{vehicle.startDate || '-'}</td>
                    <td>{vehicle.endDate || '-'}</td>
                    <td>{vehicle.totalVisits}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditVehicle(vehicle)}
                        className="me-1"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteVehicle(vehicle.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (
                page === currentPage - 3 ||
                page === currentPage + 3
              ) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}
            
            <Pagination.Next 
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* 新增/編輯車輛模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? '編輯車輛' : '新增車輛'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車牌號碼 *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入車牌號碼"
                    value={formData.plateNumber || ''}
                    onChange={(e) => setFormData({...formData, plateNumber: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車主姓名 *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入車主姓名"
                    value={formData.ownerName || ''}
                    onChange={(e) => setFormData({...formData, ownerName: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>聯絡電話 *</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="請輸入聯絡電話"
                    value={formData.ownerPhone || ''}
                    onChange={(e) => setFormData({...formData, ownerPhone: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>地址</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入地址"
                    value={formData.ownerAddress || ''}
                    onChange={(e) => setFormData({...formData, ownerAddress: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車輛類型 *</Form.Label>
                  <Form.Select
                    value={formData.vehicleType || 'car'}
                    onChange={(e) => setFormData({...formData, vehicleType: e.target.value as any})}
                    required
                  >
                    <option value="car">汽車</option>
                    <option value="motorcycle">機車</option>
                    <option value="truck">卡車</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>指定車位</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入車位編號"
                    value={formData.assignedSpaceId || ''}
                    onChange={(e) => setFormData({...formData, assignedSpaceId: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>品牌</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入品牌"
                    value={formData.brand || ''}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>型號</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入型號"
                    value={formData.model || ''}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>顏色</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入顏色"
                    value={formData.color || ''}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>狀態</Form.Label>
                  <Form.Select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                  >
                    <option value="active">正常</option>
                    <option value="vip">VIP</option>
                    <option value="blacklisted">黑名單</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="共用車位"
                    checked={formData.isSharedSpace || false}
                    onChange={(e) => setFormData({...formData, isSharedSpace: e.target.checked})}
                    className="mt-4"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {/* 共用車位額外車牌管理 */}
            {formData.isSharedSpace && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>額外車牌號碼</Form.Label>
                    <div className="border rounded p-3 bg-light">
                      {formData.additionalPlates.map((plate, index) => (
                        <div key={index} className="d-flex align-items-center mb-2">
                          <Form.Control
                            type="text"
                            placeholder="請輸入車牌號碼"
                            value={plate}
                            onChange={(e) => {
                              const newPlates = [...formData.additionalPlates];
                              newPlates[index] = e.target.value;
                              setFormData({...formData, additionalPlates: newPlates});
                            }}
                            className="me-2"
                          />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              const newPlates = formData.additionalPlates.filter((_, i) => i !== index);
                              setFormData({...formData, additionalPlates: newPlates});
                            }}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          setFormData({
                            ...formData, 
                            additionalPlates: [...formData.additionalPlates, '']
                          });
                        }}
                      >
                        <i className="fas fa-plus me-1"></i>
                        新增車牌
                      </Button>
                      {formData.additionalPlates.length === 0 && (
                        <p className="text-muted mb-0 mt-2">
                          <small>點擊「新增車牌」為此共用車位新增其他車牌號碼</small>
                        </p>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>開始日期</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.startDate || ''}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>結束日期</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.endDate || ''}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>備註</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="請輸入備註"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveVehicle}
            disabled={!formData.plateNumber || !formData.ownerName || !formData.ownerPhone}
          >
            {isEditing ? '更新' : '新增'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 匯入車輛模態框 */}
       <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
         <Modal.Header closeButton>
           <Modal.Title>匯入車輛資料</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <Form>
             <Form.Group className="mb-3">
               <Form.Label>選擇檔案</Form.Label>
               <Form.Control
                 type="file"
                 accept=".csv,.xlsx,.xls"
                 onChange={(e) => {
                   const file = (e.target as HTMLInputElement).files?.[0];
                   setImportFile(file || null);
                 }}
               />
               <Form.Text className="text-muted">
                 支援格式：CSV、Excel (.xlsx, .xls)
               </Form.Text>
             </Form.Group>

             <div className="mb-3">
               <h6>下載範本檔案：</h6>
               <Button 
                 variant="outline-primary" 
                 size="sm" 
                 className="me-2"
                 onClick={() => handleDownloadTemplate('csv')}
               >
                 <i className="fas fa-download me-1"></i>
                 CSV 範本
               </Button>
               <Button 
                 variant="outline-success" 
                 size="sm"
                 onClick={() => handleDownloadTemplate('excel')}
               >
                 <i className="fas fa-download me-1"></i>
                 Excel 範本
               </Button>
             </div>
             
             <Alert variant="info">
               <h6>檔案格式說明：</h6>
               <p className="mb-2">請確保檔案包含以下欄位：</p>
               <ul className="mb-0">
                 <li><strong>車牌號碼</strong> (必填) - plateNumber</li>
                 <li><strong>車主姓名</strong> (必填) - ownerName</li>
                 <li><strong>聯絡電話</strong> (必填) - ownerPhone</li>
                 <li><strong>車輛類型</strong> (必填) - vehicleType (car/motorcycle/truck)</li>
                 <li><strong>品牌</strong> (選填) - brand</li>
                 <li><strong>型號</strong> (選填) - model</li>
                 <li><strong>顏色</strong> (選填) - color</li>
                 <li><strong>狀態</strong> (選填) - status (active/vip/blacklisted，預設為 active)</li>
               </ul>
             </Alert>
           </Form>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={() => setShowImportModal(false)}>
             取消
           </Button>
           <Button 
             variant="primary" 
             disabled={!importFile || importLoading}
             onClick={handleImportVehicles}
           >
             {importLoading ? (
               <>
                 <Spinner animation="border" size="sm" className="me-2" />
                 匯入中...
               </>
             ) : (
               <>
                 <i className="fas fa-upload me-2"></i>
                 開始匯入
               </>
             )}
           </Button>
         </Modal.Footer>
       </Modal>
    </div>
  );
};

export default VehicleRegistration;